// Libs
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Local
import { DbRead, DbWrite } from "../../database";
import { ExistingPurchaseOrder, OPOStatus } from "../../models/purchase-order";
import { ExistingVendor } from "../../models/vendor";
import { ExistingCraftRecord } from "../../models/craft-record";
import { ExistingPurchaseOrderReceipt } from "../../models/purchase-order-receipt";
import { PURCHASE_ORDERS_URL, WORK_RECORD_AND_TASKS_URL } from "../../urls";
import { useAuthStore } from "../../store/firebase-auth";
import ViewPurchaseOrderPage from "./ViewPurchaseOrderPage";
import { createToastMessageID } from "../../utils";
import { useToastMessageStore } from "../../store/toast-messages";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import EditDeliveryInfoDialog from "../../components/EditDeliveryInfoDialog";
import EditNotesDialog from "../../components/EditNotesDialog";
import EditItemsDialog from "../../components/EditItemsDialog";
import EditDatesAndTermsDialog from "../../components/EditDatesAndTermsDialog";
import CreateReceiptDialog, {
  CreateReceiptFormValues,
} from "../../components/purchaseOrders/CreateReceiptDialog";
import EditReceiptDialog, {
  EditReceiptFormValues,
} from "../../components/purchaseOrders/EditReceiptDialog";
import SendPurchaseOrderEmailDialog from "../../components/purchaseOrders/SendPurchaseOrderEmailDialog";
import { convertJSDateToFSTimestamp } from "../../utils/convertJSDateToFSTimestamp";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";

interface Props {
  siteKey: string;
}

export default function ViewPurchaseOrderContainer({ siteKey }: Props) {
  const { purchaseOrderId } = useParams<{ purchaseOrderId: string }>();
  const [purchaseOrder, setPurchaseOrder] =
    useState<ExistingPurchaseOrder | null>(null);
  const [vendor, setVendor] = useState<ExistingVendor | null>(null);
  const [job, setJob] = useState<ExistingCraftRecord | null>(null);
  const [receipts, setReceipts] = useState<ExistingPurchaseOrderReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkJobDialogOpen, setLinkJobDialogOpen] = useState(false);
  const [availableJobs, setAvailableJobs] = useState<ExistingCraftRecord[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [linkingJobId, setLinkingJobId] = useState<string | null>(null);
  const [unlinkingJob, setUnlinkingJob] = useState(false);
  const [confirmLinkDialogOpen, setConfirmLinkDialogOpen] = useState(false);
  const [confirmUnlinkDialogOpen, setConfirmUnlinkDialogOpen] = useState(false);
  const [selectedJobToLink, setSelectedJobToLink] =
    useState<ExistingCraftRecord | null>(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [editNotesDialogOpen, setEditNotesDialogOpen] = useState(false);
  const [editDeliveryDialogOpen, setEditDeliveryDialogOpen] = useState(false);
  const [editItemsDialogOpen, setEditItemsDialogOpen] = useState(false);
  const [editDatesAndTermsDialogOpen, setEditDatesAndTermsDialogOpen] =
    useState(false);

  // Receipt-related state
  const [createReceiptDialogOpen, setCreateReceiptDialogOpen] = useState(false);
  const [editReceiptDialogOpen, setEditReceiptDialogOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] =
    useState<ExistingPurchaseOrderReceipt | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [confirmDeleteReceiptDialogOpen, setConfirmDeleteReceiptDialogOpen] =
    useState(false);
  const [receiptToDelete, setReceiptToDelete] =
    useState<ExistingPurchaseOrderReceipt | null>(null);
  const [emailPurchaseOrderDialogOpen, setEmailPurchaseOrderDialogOpen] =
    useState(false);

  // State for receive all functionality
  const [receiveAllInitialValues, setReceiveAllInitialValues] = useState<
    Partial<CreateReceiptFormValues> | undefined
  >(undefined);

  // Status management state
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [confirmCancelDialogOpen, setConfirmCancelDialogOpen] = useState(false);

  const firebaseUser = useAuthStore((state) => state.firebaseUser);
  const navigate = useNavigate();
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );

  useEffect(() => {
    async function fetchPurchaseOrderData() {
      if (!purchaseOrderId) {
        setError("Purchase order ID is missing");
        setLoading(false);
        return;
      }

      try {
        // Fetch purchase order
        await DbRead.purchaseOrders.subscribe(
          siteKey,
          purchaseOrderId,
          (po: ExistingPurchaseOrder | null) => {
            setPurchaseOrder(po);
          },
        );

        setLoading(false);
      } catch (error) {
        console.error("Error fetching purchase order:", error);
        setError("Error loading purchase order data");
        setLoading(false);
      }
    }

    fetchPurchaseOrderData();
  }, [siteKey, purchaseOrderId]);

  useEffect(() => {
    async function fetchVendorData() {
      if (!purchaseOrder) return;
      await DbRead.vendors.subscribe(
        siteKey,
        purchaseOrder.vendorID,
        (vendor: ExistingVendor | null) => {
          setVendor(vendor);
        },
      );
    }

    fetchVendorData();
  }, [purchaseOrder]);

  useEffect(() => {
    async function fetchJobData() {
      if (!purchaseOrder?.craftRecordID) {
        setJob(null);
        return;
      }

      try {
        const unsubscribe = DbRead.parentRecords.subscribe(
          siteKey,
          purchaseOrder.craftRecordID,
          (jobDoc: ExistingCraftRecord | undefined) => {
            setJob(jobDoc || null);
          },
        );
        return unsubscribe;
      } catch (error) {
        console.error("Error fetching job data:", error);
        setJob(null);
      }
    }

    fetchJobData();
  }, [purchaseOrder, siteKey]);

  // Fetch receipts for this purchase order
  useEffect(() => {
    if (!purchaseOrderId) return;

    // Subscribe to receipts for real-time updates
    const unsubscribe = DbRead.purchaseOrderReceipts.subscribeByPurchaseOrderId(
      siteKey,
      purchaseOrderId,
      (receiptsData: ExistingPurchaseOrderReceipt[]) => {
        setReceipts(receiptsData);
      },
    );

    // Return cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [siteKey, purchaseOrderId]);

  const fetchJobs = async (
    page: number,
    searchQuery: string,
    loadMore: boolean,
  ) => {
    setJobsLoading(true);
    try {
      const jobs = await DbRead.parentRecords.getAll(siteKey, true, null, 1000);

      // Filter jobs based on search query
      let filteredJobs = jobs;
      if (searchQuery.trim()) {
        const searchLower = searchQuery.toLowerCase();
        filteredJobs = jobs.filter(
          (job) =>
            job.title.toLowerCase().includes(searchLower) ||
            (job.description &&
              job.description.toLowerCase().includes(searchLower)),
        );
      }

      setAvailableJobs(filteredJobs);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Error loading jobs",
        dialog: false,
        type: "error",
      });
    } finally {
      setJobsLoading(false);
    }
  };

  const navigateBack = () => {
    navigate(PURCHASE_ORDERS_URL);
  };

  // Navigate to job page if purchase order has a craftRecordID
  const navigateToJob = () => {
    if (purchaseOrder?.craftRecordID) {
      navigate(`${WORK_RECORD_AND_TASKS_URL}/${purchaseOrder.craftRecordID}`);
    }
  };

  // Handle "Receive Items" action
  const handleReceiveItems = () => {
    setReceiveAllInitialValues(undefined); // Clear any previous initial values
    setCreateReceiptDialogOpen(true);
  };

  // Handle "Receive All" action - creates a receipt with all remaining items
  const handleReceiveAll = () => {
    if (!purchaseOrder) return;

    // Calculate remaining quantities for each item
    const getItemReceivedQuantity = (itemId: string) => {
      return receipts.reduce((total, receipt) => {
        const receiptItem = receipt.lineItems.find(
          (item) => item.purchaseOrderItemID === itemId,
        );
        return total + (receiptItem?.quantityReceived || 0);
      }, 0);
    };

    // Create line items with remaining quantities
    const lineItemsWithRemainingQty = purchaseOrder.lineItems
      .map((item) => {
        const receivedQty = getItemReceivedQuantity(item.id);
        const remainingQty = item.quantity - receivedQty;

        return {
          purchaseOrderItemID: item.id,
          title: item.title,
          description: item.description,
          quantityOrdered: item.quantity,
          quantityReceived: remainingQty, // Set to remaining quantity
          quantityRejected: 0,
          rejectionReason: "",
          unitPrice: item.unitPrice,
          priceBookItemID: item.priceBookItemID,
        };
      })
      .filter((item) => item.quantityReceived > 0); // Only include items with remaining quantity

    if (lineItemsWithRemainingQty.length === 0) {
      addToastMessage({
        id: createToastMessageID(),
        message: "All items have already been received",
        dialog: false,
        type: "info",
      });
      return;
    }

    // Set initial values for the dialog
    const initialValues: Partial<CreateReceiptFormValues> = {
      invoiceNumber: "",
      receivedDate: new Date(),
      receivedBy: "",
      lineItems: lineItemsWithRemainingQty,
    };

    setReceiveAllInitialValues(initialValues);
    setCreateReceiptDialogOpen(true);
  };

  // Handle "Email" action
  const handleEmailPurchaseOrder = () => {
    setEmailPurchaseOrderDialogOpen(true);
  };

  // Handle "Generate PDF" action
  const handleGeneratePDF = async () => {
    if (!purchaseOrder || generatingPDF) return;

    setGeneratingPDF(true);

    try {
      const pdfUrl = await DbRead.purchaseOrders.getPDF(
        siteKey,
        purchaseOrder.id,
      );

      // Open PDF in new tab
      window.open(pdfUrl, "_blank");

      addToastMessage({
        id: createToastMessageID(),
        message: "PDF generated successfully",
        dialog: false,
        type: "success",
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Error generating PDF",
        dialog: false,
        type: "error",
      });
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Receipt handlers
  const handleCreateReceipt = async (formValues: CreateReceiptFormValues) => {
    if (!purchaseOrder || !firebaseUser) return;

    setReceiptLoading(true);

    try {
      // Convert date to ISO string
      const receivedDate = formValues.receivedDate.toISOString();

      // Calculate total quantity received
      const totalQuantityReceived = formValues.lineItems.reduce(
        (total, item) => total + item.quantityReceived,
        0,
      );

      const receiptData = {
        siteKey,
        purchaseOrderID: purchaseOrder.id,
        invoiceNumber: formValues.invoiceNumber,
        vendorID: purchaseOrder.vendorID,
        vendorName: vendor?.name || "Unknown Vendor",
        locationID: purchaseOrder.locationID,
        receivedDate,
        receivedBy: formValues.receivedBy,
        receivingNotes: formValues.receivingNotes || null,
        internalNotes: null,
        deleted: false,
        totalQuantityReceived,
        lineItems: formValues.lineItems.map((item) => ({
          ...item,
          rejectionReason: item.rejectionReason || null,
        })),
        customData: {},
        createdBy: firebaseUser.uid,
        lastModifiedBy: firebaseUser.uid,
      };

      await DbWrite.purchaseOrderReceipts.create(receiptData);

      addToastMessage({
        id: createToastMessageID(),
        message: "Receipt created successfully",
        dialog: false,
        type: "success",
      });
    } catch (error) {
      console.error("Error creating receipt:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Error creating receipt",
        dialog: false,
        type: "error",
      });
    } finally {
      setReceiptLoading(false);
    }
  };

  const handleEditReceipt = (receipt: ExistingPurchaseOrderReceipt) => {
    setSelectedReceipt(receipt);
    setEditReceiptDialogOpen(true);
  };

  const handleUpdateReceipt = async (formValues: EditReceiptFormValues) => {
    if (!selectedReceipt || !firebaseUser) return;

    setReceiptLoading(true);

    try {
      // Convert date to Firestore timestamp
      const receivedDate = convertJSDateToFSTimestamp(formValues.receivedDate);

      // Calculate total quantity received
      const totalQuantityReceived = formValues.lineItems.reduce(
        (total, item) => total + item.quantityReceived,
        0,
      );

      const updateData = {
        id: selectedReceipt.id,
        refPath: selectedReceipt.refPath,
        invoiceNumber: formValues.invoiceNumber,
        receivedDate,
        receivedBy: formValues.receivedBy,
        receivingNotes: formValues.receivingNotes || null,
        totalQuantityReceived,
        lineItems: formValues.lineItems.map((item) => ({
          ...item,
          rejectionReason: item.rejectionReason || null,
        })),
        lastModifiedBy: firebaseUser.uid,
      };

      await DbWrite.purchaseOrderReceipts.update(updateData);

      addToastMessage({
        id: createToastMessageID(),
        message: "Receipt updated successfully",
        dialog: false,
        type: "success",
      });
    } catch (error) {
      console.error("Error updating receipt:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Error updating receipt",
        dialog: false,
        type: "error",
      });
    } finally {
      setReceiptLoading(false);
    }
  };

  const handleViewReceipt = (receipt: ExistingPurchaseOrderReceipt) => {
    // For now, just expand the receipt in the list
    // Could implement a dedicated view dialog later
    console.log("View receipt:", receipt);
  };

  const handleEditVendorInfo = () => {
    // Navigate to vendor edit page or open vendor edit dialog
    if (vendor) {
      navigate(`/vendors/${vendor.id}`);
    }
  };

  const handleEditDeliveryInfo = () => {
    setEditDeliveryDialogOpen(true);
  };

  const handleEditItems = () => {
    setEditItemsDialogOpen(true);
  };

  const handleEditNotes = () => {
    setEditNotesDialogOpen(true);
  };

  const handleEditDatesAndTerms = () => {
    setEditDatesAndTermsDialogOpen(true);
  };

  const handleLinkToJob = async () => {
    setLinkJobDialogOpen(true);
    setSearchQuery("");
    await fetchJobs(1, "", true);
  };

  const handleSearchChange = async (newSearch: string) => {
    setSearchQuery(newSearch);
    await fetchJobs(1, newSearch, true);
  };

  const handleCloseJobDialog = () => {
    setLinkJobDialogOpen(false);
    setSearchQuery("");
    setAvailableJobs([]);
    setLinkingJobId(null);
    setSelectedJobToLink(null);
    setConfirmLinkDialogOpen(false);
  };

  const handleJobSelected = async (selectedJob: ExistingCraftRecord) => {
    if (!purchaseOrder) return;

    setSelectedJobToLink(selectedJob);
    setConfirmLinkDialogOpen(true);
  };

  const handleConfirmLinkJob = async () => {
    if (!purchaseOrder || !selectedJobToLink) return;

    setLinkingJobId(selectedJobToLink.id);
    setConfirmLinkDialogOpen(false);

    try {
      await DbWrite.purchaseOrders.update({
        id: purchaseOrder.id,
        refPath: purchaseOrder.refPath,
        craftRecordID: selectedJobToLink.id,
        lastModifiedBy: firebaseUser?.uid || "unknown",
      });

      addToastMessage({
        id: createToastMessageID(),
        message: `Purchase order linked to job: ${selectedJobToLink.title}`,
        dialog: false,
        type: "success",
      });

      handleCloseJobDialog();
    } catch (error) {
      console.error("Error linking purchase order to job:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Error linking purchase order to job",
        dialog: false,
        type: "error",
      });
    } finally {
      setLinkingJobId(null);
      setSelectedJobToLink(null);
    }
  };

  const handleUnlinkJob = async () => {
    if (!purchaseOrder || !job) return;

    setConfirmUnlinkDialogOpen(true);
  };

  const handleConfirmUnlinkJob = async () => {
    if (!purchaseOrder || !job) return;

    setUnlinkingJob(true);
    setConfirmUnlinkDialogOpen(false);

    try {
      await DbWrite.purchaseOrders.update({
        id: purchaseOrder.id,
        refPath: purchaseOrder.refPath,
        craftRecordID: null,
        lastModifiedBy: firebaseUser?.uid || "unknown",
      });

      addToastMessage({
        id: createToastMessageID(),
        message: `Purchase order unlinked from job: ${job.title}`,
        dialog: false,
        type: "success",
      });
    } catch (error) {
      console.error("Error unlinking purchase order from job:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Error unlinking purchase order from job",
        dialog: false,
        type: "error",
      });
    } finally {
      setUnlinkingJob(false);
    }
  };

  const handleDeleteReceipt = (receipt: ExistingPurchaseOrderReceipt) => {
    setReceiptToDelete(receipt);
    setConfirmDeleteReceiptDialogOpen(true);
  };

  const handleConfirmDeleteReceipt = async () => {
    if (!receiptToDelete || !firebaseUser) return;

    setReceiptLoading(true);

    try {
      await DbWrite.purchaseOrderReceipts.delete(receiptToDelete.id, siteKey);

      addToastMessage({
        id: createToastMessageID(),
        message: "Receipt deleted successfully",
        dialog: false,
        type: "success",
      });

      setConfirmDeleteReceiptDialogOpen(false);
      setReceiptToDelete(null);
    } catch (error) {
      console.error("Error deleting receipt:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Error deleting receipt",
        dialog: false,
        type: "error",
      });
    } finally {
      setReceiptLoading(false);
    }
  };

  const handleSendPurchaseOrderEmail = async (
    emailList: string[],
    emailBody: string,
  ) => {
    if (!purchaseOrder || !vendor) return;

    try {
      await DbWrite.purchaseOrders.email(
        siteKey,
        purchaseOrder.id,
        emailList,
        emailBody,
      );

      addToastMessage({
        id: createToastMessageID(),
        message: `Purchase order sent successfully to: ${emailList.join(", ")}`,
        dialog: false,
        type: "success",
      });

      setEmailPurchaseOrderDialogOpen(false);
    } catch (error) {
      console.error("Error sending purchase order email:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Error sending purchase order email",
        dialog: false,
        type: "error",
      });
      throw error; // Re-throw so the dialog can handle the error state
    }
  };

  // Handle changing status from DRAFT to PENDING
  const handleChangeToPending = async () => {
    if (!purchaseOrder || !firebaseUser) return;

    setStatusUpdateLoading(true);

    try {
      await DbWrite.purchaseOrders.update({
        id: purchaseOrder.id,
        refPath: purchaseOrder.refPath,
        status: OPOStatus.PENDING,
        lastModifiedBy: firebaseUser.uid,
      });

      addToastMessage({
        id: createToastMessageID(),
        message: "Purchase order status changed to Pending",
        dialog: false,
        type: "success",
      });
    } catch (error) {
      console.error("Error updating purchase order status:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Error updating purchase order status",
        dialog: false,
        type: "error",
      });
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  // Handle canceling purchase order
  const handleCancelPurchaseOrder = () => {
    setConfirmCancelDialogOpen(true);
  };

  const handleConfirmCancelPurchaseOrder = async () => {
    if (!purchaseOrder || !firebaseUser) return;

    setStatusUpdateLoading(true);
    setConfirmCancelDialogOpen(false);

    try {
      await DbWrite.purchaseOrders.update({
        id: purchaseOrder.id,
        refPath: purchaseOrder.refPath,
        status: OPOStatus.CANCELED,
        lastModifiedBy: firebaseUser.uid,
      });

      addToastMessage({
        id: createToastMessageID(),
        message: "Purchase order has been canceled",
        dialog: false,
        type: "success",
      });
    } catch (error) {
      console.error("Error canceling purchase order:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Error canceling purchase order",
        dialog: false,
        type: "error",
      });
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <p className="mb-4 text-red-600">
          {error || "Purchase order not found"}
        </p>
        <button
          onClick={() => navigate(PURCHASE_ORDERS_URL)}
          className="hover:bg-primary-dark rounded bg-primary px-4 py-2 text-white"
        >
          Return to Purchase Orders
        </button>
      </div>
    );
  }

  if (loading || !purchaseOrder) {
    return (
      <div className="flex h-full flex-auto flex-col items-center justify-center">
        <LoadingClipboardAnimation />
      </div>
    );
  }

  return (
    <>
      <ViewPurchaseOrderPage
        purchaseOrder={purchaseOrder}
        siteKey={siteKey}
        vendor={vendor}
        job={job}
        receipts={receipts}
        navigateBack={navigateBack}
        navigateToJob={navigateToJob}
        handleLinkToJob={handleLinkToJob}
        handleUnlinkJob={handleUnlinkJob}
        unlinkingJob={unlinkingJob}
        handleReceiveItems={handleReceiveItems}
        handleReceiveAll={handleReceiveAll}
        handleEmailPurchaseOrder={handleEmailPurchaseOrder}
        handleGeneratePDF={handleGeneratePDF}
        generatingPDF={generatingPDF}
        handleEditVendorInfo={handleEditVendorInfo}
        handleEditDeliveryInfo={handleEditDeliveryInfo}
        handleEditItems={handleEditItems}
        handleEditNotes={handleEditNotes}
        handleEditDatesAndTerms={handleEditDatesAndTerms}
        onCreateReceipt={() => setCreateReceiptDialogOpen(true)}
        onEditReceipt={handleEditReceipt}
        onViewReceipt={handleViewReceipt}
        onDeleteReceipt={handleDeleteReceipt}
        receiptLoading={receiptLoading}
        handleChangeToPending={handleChangeToPending}
        handleCancelPurchaseOrder={handleCancelPurchaseOrder}
        statusUpdateLoading={statusUpdateLoading}
      />

      {/* Job Selection Dialog */}
      {linkJobDialogOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-25"
              onClick={handleCloseJobDialog}
            />
            <div className="relative max-h-[80vh] w-full max-w-4xl overflow-hidden rounded-lg bg-white shadow-xl">
              <div className="border-b p-6">
                <h3 className="text-lg font-medium">Select Job to Link</h3>
                <button
                  onClick={handleCloseJobDialog}
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              {/* Search Bar */}
              <div className="border-b bg-gray-50 p-4">
                <input
                  type="text"
                  placeholder="Search jobs by title or description..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="max-h-[60vh] overflow-y-auto p-6">
                {jobsLoading && availableJobs.length === 0 ? (
                  <div className="flex justify-center py-8">
                    <div className="text-gray-500">Loading jobs...</div>
                  </div>
                ) : availableJobs.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    {searchQuery
                      ? `No jobs found matching "${searchQuery}"`
                      : "No open jobs found"}
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {availableJobs.map((job) => (
                        <div
                          key={job.id}
                          onClick={() =>
                            linkingJobId !== job.id && handleJobSelected(job)
                          }
                          className={`cursor-pointer rounded-lg border border-gray-200 p-4 transition-colors hover:bg-gray-50 ${
                            linkingJobId === job.id
                              ? "cursor-not-allowed opacity-50"
                              : ""
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">
                                {job.title}
                              </div>
                              <div className="text-sm text-gray-500">
                                Created:{" "}
                                {new Date(
                                  job.timestampRecordCreated.toMillis(),
                                ).toLocaleDateString()}
                              </div>
                              {job.description && (
                                <div className="mt-1 truncate text-sm text-gray-600">
                                  {job.description}
                                </div>
                              )}
                            </div>
                            {linkingJobId === job.id && (
                              <div className="ml-4 flex items-center">
                                <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-blue-600"></div>
                                <span className="ml-2 text-sm text-blue-600">
                                  Linking...
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Results Info */}
                    <div className="mt-4 text-center text-sm text-gray-500">
                      Showing {availableJobs.length} jobs
                      {searchQuery && ` matching "${searchQuery}"`}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Linking Job */}
      <ConfirmationDialog
        isOpen={confirmLinkDialogOpen}
        onClose={() => {
          setConfirmLinkDialogOpen(false);
          setSelectedJobToLink(null);
        }}
        handleConfirmAction={handleConfirmLinkJob}
        isSubmitting={linkingJobId !== null}
        title="Link Purchase Order to Job"
        body={
          selectedJobToLink ? (
            <div>
              <p>
                Are you sure you want to link this purchase order to the
                following job?
              </p>
              <div className="mt-4 rounded-md bg-gray-50 p-4">
                <p className="font-medium">{selectedJobToLink.title}</p>
                {selectedJobToLink.description && (
                  <p className="mt-1 text-sm text-gray-600">
                    {selectedJobToLink.description}
                  </p>
                )}
              </div>
            </div>
          ) : (
            ""
          )
        }
      />

      {/* Confirmation Dialog for Unlinking Job */}
      <ConfirmationDialog
        isOpen={confirmUnlinkDialogOpen}
        onClose={() => setConfirmUnlinkDialogOpen(false)}
        handleConfirmAction={handleConfirmUnlinkJob}
        isSubmitting={unlinkingJob}
        title="Unlink Purchase Order from Job"
        body={
          job ? (
            <div>
              <p>
                Are you sure you want to unlink this purchase order from the
                following job?
              </p>
              <div className="mt-4 rounded-md bg-gray-50 p-4">
                <p className="font-medium">{job.title}</p>
                {job.description && (
                  <p className="mt-1 text-sm text-gray-600">
                    {job.description}
                  </p>
                )}
              </div>
            </div>
          ) : (
            ""
          )
        }
      />

      {/* Edit Notes Dialog */}
      {editNotesDialogOpen && purchaseOrder && (
        <EditNotesDialog
          isOpen={editNotesDialogOpen}
          onClose={() => setEditNotesDialogOpen(false)}
          purchaseOrder={purchaseOrder}
          siteKey={siteKey}
        />
      )}

      {/* Edit Delivery Info Dialog */}
      {editDeliveryDialogOpen && purchaseOrder && (
        <EditDeliveryInfoDialog
          isOpen={editDeliveryDialogOpen}
          onClose={() => setEditDeliveryDialogOpen(false)}
          purchaseOrder={purchaseOrder}
          siteKey={siteKey}
        />
      )}

      {/* Edit Items Dialog */}
      {editItemsDialogOpen && purchaseOrder && (
        <EditItemsDialog
          isOpen={editItemsDialogOpen}
          onClose={() => setEditItemsDialogOpen(false)}
          purchaseOrder={purchaseOrder}
          siteKey={siteKey}
        />
      )}

      {/* Edit Dates and Terms Dialog */}
      {editDatesAndTermsDialogOpen && purchaseOrder && (
        <EditDatesAndTermsDialog
          isOpen={editDatesAndTermsDialogOpen}
          onClose={() => setEditDatesAndTermsDialogOpen(false)}
          purchaseOrder={purchaseOrder}
          siteKey={siteKey}
        />
      )}

      {/* Create Receipt Dialog */}
      {createReceiptDialogOpen && purchaseOrder && (
        <CreateReceiptDialog
          isDialogOpen={createReceiptDialogOpen}
          closeDialog={() => {
            setCreateReceiptDialogOpen(false);
            setReceiveAllInitialValues(undefined);
          }}
          handleSave={handleCreateReceipt}
          purchaseOrder={purchaseOrder}
          receipts={receipts}
          loading={receiptLoading}
          initialValues={receiveAllInitialValues}
        />
      )}

      {/* Edit Receipt Dialog */}
      {editReceiptDialogOpen && selectedReceipt && purchaseOrder && (
        <EditReceiptDialog
          isDialogOpen={editReceiptDialogOpen}
          closeDialog={() => setEditReceiptDialogOpen(false)}
          handleSave={handleUpdateReceipt}
          receipt={selectedReceipt}
          purchaseOrder={purchaseOrder}
          receipts={receipts}
          loading={receiptLoading}
        />
      )}

      {/* Confirmation Dialog for Deleting Receipt */}
      <ConfirmationDialog
        isOpen={confirmDeleteReceiptDialogOpen}
        onClose={() => {
          setConfirmDeleteReceiptDialogOpen(false);
          setReceiptToDelete(null);
        }}
        handleConfirmAction={handleConfirmDeleteReceipt}
        isSubmitting={receiptLoading}
        title="Delete Receipt"
        body={`Are you sure you want to delete this receipt? This action cannot be undone.`}
      />

      {/* Email Purchase Order Dialog */}
      {emailPurchaseOrderDialogOpen && vendor && purchaseOrder && (
        <SendPurchaseOrderEmailDialog
          isDialogOpen={emailPurchaseOrderDialogOpen}
          closeDialog={() => setEmailPurchaseOrderDialogOpen(false)}
          customerEmailList={vendor.email || []}
          purchaseOrderNumber={purchaseOrder.poNumber}
          purchaseOrderStatus={purchaseOrder.status}
          defaultEmailBody={`Please find attached Purchase Order #${purchaseOrder.poNumber} for your review.\n\nThank you for your business.`}
          sendPurchaseOrderEmail={handleSendPurchaseOrderEmail}
        />
      )}

      {/* Confirmation Dialog for Canceling Purchase Order */}
      <ConfirmationDialog
        isOpen={confirmCancelDialogOpen}
        onClose={() => setConfirmCancelDialogOpen(false)}
        handleConfirmAction={handleConfirmCancelPurchaseOrder}
        isSubmitting={statusUpdateLoading}
        title="Cancel Purchase Order"
        body="Are you sure you want to cancel this purchase order? This action will change its status to Canceled."
      />
    </>
  );
}
